import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Upload, X } from "lucide-react";
import { Button } from "./ui/button";
import { API, useAuth } from "../App";
import { formatApiError } from "../lib/utils";

const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/gif"];

export async function uploadImage(file) {
  if (!ALLOWED.includes(file.type)) throw new Error("Please upload a JPEG, PNG, WebP or GIF image");
  if (file.size > 5 * 1024 * 1024) throw new Error("Image must be less than 5MB");
  const fd = new FormData();
  fd.append("file", file);
  try {
    const r = await axios.post(`${API}/upload/image`, fd, { withCredentials: true });
    return r.data.url;
  } catch (e) {
    throw new Error(formatApiError(e, "Upload failed"));
  }
}

export const CompanyLogoField = ({ onChanged }) => {
  const { user, checkAuth } = useAuth();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async (logo) => {
    await axios.put(`${API}/users/me/company-logo`, { company_logo: logo }, { withCredentials: true });
    await checkAuth();
    onChanged?.();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    setBusy(true);
    try {
      await save(await uploadImage(file));
      toast.success("Company logo updated");
    } catch (err) {
      setError(err.response ? formatApiError(err) : err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="company-logo-field">
      <p className="text-sm text-zinc-300 mb-2">Company Logo</p>
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 bg-zinc-800 border border-zinc-700 flex items-center justify-center overflow-hidden">
          {user?.company_logo ? (
            <img src={user.company_logo} alt="Company logo" className="max-w-full max-h-full object-contain" data-testid="company-logo-preview" />
          ) : (
            <Building2 className="h-8 w-8 text-zinc-600" />
          )}
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" className="border-zinc-700" disabled={busy}
            onClick={() => inputRef.current?.click()} data-testid="company-logo-upload-btn">
            <Upload className="h-4 w-4 mr-2" /> {busy ? "Uploading…" : user?.company_logo ? "Replace" : "Upload logo"}
          </Button>
          {user?.company_logo && (
            <Button type="button" variant="ghost" className="text-zinc-400 hover:text-red-400" disabled={busy}
              onClick={() => save(null)} data-testid="company-logo-remove-btn">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <input ref={inputRef} type="file" accept={ALLOWED.join(",")} className="hidden" onChange={handleFile} data-testid="company-logo-input" />
      </div>
      {error && <p className="text-sm text-red-400 mt-2" role="alert" data-testid="company-logo-error">{error}</p>}
      <p className="text-xs text-zinc-500 mt-2">Shown on your public profile. PNG with transparent background works best (max 5MB).</p>
    </div>
  );
};
